import React, { useState } from "react";
import { AlertTriangle, Loader2, X } from "lucide-react";
import { Button } from "./ui/button";

/**
 * Double-confirmation removal dialog. The destructive action stays disabled
 * until the operator types the subject's exact name — prevents accidental
 * deletes of staff members.
 */
export function ConfirmRemoveModal({
  open,
  title = "Remove staff member",
  name,
  description,
  confirmLabel = "Remove",
  onConfirm,
  onClose,
}) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  const matches = typed.trim().toLowerCase() === (name || "").trim().toLowerCase();

  const handleConfirm = async () => {
    if (!matches || busy) return;
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-900/55 backdrop-blur-sm p-4"
      onClick={() => !busy && onClose()}
      data-testid="confirm-remove-modal"
    >
      <div
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 px-5 py-4 border-b border-slate-100">
          <div className="h-10 w-10 rounded-full bg-red-50 flex items-center justify-center shrink-0">
            <AlertTriangle className="h-5 w-5 text-red-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[15px] font-bold text-slate-900">{title}</div>
            <div className="text-[12.5px] text-slate-500 mt-0.5">
              {description || "This permanently removes the staff member and frees their seat at the next billing cycle. This cannot be undone."}
            </div>
          </div>
          <button onClick={() => !busy && onClose()} className="h-7 w-7 rounded-md hover:bg-slate-100 flex items-center justify-center shrink-0" data-testid="confirm-remove-x">
            <X className="h-4 w-4 text-slate-400" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-2.5">
          <label className="block text-[12.5px] text-slate-600">
            Type <span className="font-bold text-slate-900">{name}</span> to confirm
          </label>
          <input
            autoFocus
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={name}
            data-testid="confirm-remove-input"
            className="w-full h-10 px-3 rounded-lg border border-slate-300 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-400"
          />
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 bg-slate-50 border-t border-slate-100">
          <Button variant="outline" onClick={() => !busy && onClose()} data-testid="confirm-remove-cancel" className="h-9 text-[13px]">
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!matches || busy}
            data-testid="confirm-remove-confirm"
            className="h-9 bg-red-600 hover:bg-red-700 text-white text-[13px] disabled:opacity-40"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
            {busy ? "Removing…" : confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ConfirmRemoveModal;
